import { Account } from '@/domain/usecases'
import { FeatureFlagName, FeatureFlagType, getFlagValue } from '@/infra/feature-flag'
import {
  makeSkillWebManagerUsersRedirectUrl,
  makeSkillWebRequestsRedirectUrl
} from '@/main/factories/http'
import { PathConstants } from '../../paths'
import {
  composeGuards,
  Guard,
  GuardContext,
  GuardResult,
  requireFeatureFlag
} from '../../shared/guards'

// ============================================
// GUARDS DO MÓDULO USER
// Bloqueio por plano/onboarding nos fluxos normais de usuário.
//
// MODELO DE ACESSO: o módulo USER é gateado por CAPABILITY (posse de pass plan),
// NÃO por `AccountModule`. Isso é intencional — um gestor (company-admin ou
// internal), classificado como BACKOFFICE por `getModule`, que também possui plano
// ativo é um usuário legítimo destas telas. Por isso aqui se checa `hasPassPlan`:
// quem tem plano entra, independentemente do módulo. Ver o "MODELO DE ACESSO" em
// `domain/usecases/account/account-profile.ts`.
//
// Observação: `requirePassPlan` também trata os redirects de "gestor sem plano",
// já que é o único ponto de entrada que decide para onde o usuário vai quando não
// tem plano ativo (precedência internal > company > system).
// ============================================

/**
 * Exige que o usuário tenha um Pass Plan.
 * Se for um gestor sem plano, redireciona para a página administrativa adequada.
 */
export function requirePassPlan(ctx: GuardContext): GuardResult {
  if (Account.hasPassPlan(ctx.account)) {
    return { allowed: true }
  }

  console.log('[Guard] User does not have pass plan')

  // Verifica se os novos fluxos estão habilitados
  const useNewAuthorizationsFlow = getFlagValue(FeatureFlagName.ENABLE_NEW_AUTHORIZATIONS_FLOW)
  const useNewCollaboratorsFlow = getFlagValue(FeatureFlagName.ENABLE_NEW_COLLABORATORS_FLOW)

  // Tratamento especial para gestores de empresa sem plano
  if (Account.isInternalCompanyManager(ctx.account)) {
    // Se o novo fluxo de autorizações estiver habilitado, vai para a página nova; senão, pedidos antigos
    const redirectUrl = useNewAuthorizationsFlow
      ? PathConstants.Routes.COMPANY_MANAGER_AUTHORIZATIONS.value
      : makeSkillWebRequestsRedirectUrl()

    return {
      allowed: false,
      redirect: redirectUrl,
      reason: `Internal company manager without pass plan - redirect to ${
        useNewAuthorizationsFlow ? 'new' : 'old'
      } requests`
    }
  }

  if (Account.isCompanyManager(ctx.account)) {
    // Se o novo fluxo de colaboradores estiver habilitado, vai para a página nova; senão, usuários antigos
    const redirectUrl = useNewCollaboratorsFlow
      ? PathConstants.Routes.COMPANY_MANAGER_COLLABORATORS.value
      : makeSkillWebManagerUsersRedirectUrl()

    return {
      allowed: false,
      redirect: redirectUrl,
      reason: `Company manager without pass plan - redirect to ${
        useNewCollaboratorsFlow ? 'new' : 'old'
      } manage users`
    }
  }

  if (Account.isSystemManager(ctx.account)) {
    // Se o usuário só tem permissão de SystemManager, redireciona para a página de Configurações do Sistema
    const redirectUrl = PathConstants.Routes.SYSTEM_MANAGER_CONFIGURATIONS.value

    return {
      allowed: false,
      redirect: redirectUrl,
      reason: `System manager without any other permissions always redirect to system configuration page`
    }
  }

  // Usuário comum sem plano - redireciona para a página dedicada
  return {
    allowed: false,
    redirect: PathConstants.Routes.NO_PLAN.value,
    reason: 'User does not have pass plan'
  }
}

/**
 * Exige que o usuário tenha concluído o onboarding.
 */
export function requireOnboardingCompleted(ctx: GuardContext): GuardResult {
  if (ctx.account.onboardingCompleted !== false) {
    return { allowed: true }
  }

  console.log('[Guard] Onboarding not completed')
  return {
    allowed: false,
    redirect: PathConstants.Routes.ONBOARDING.value,
    reason: 'Onboarding not completed'
  }
}

/**
 * Exige que o usuário tenha acesso ao onboarding.
 * O usuário precisa ter um pass plan e o onboarding não pode estar concluído.
 */
export function requireOnboardingAccess(ctx: GuardContext): GuardResult {
  const hasPassPlan = Account.hasPassPlan(ctx.account)
  const onboardingNotCompleted = ctx.account.onboardingCompleted === false

  if (hasPassPlan && onboardingNotCompleted) {
    return { allowed: true }
  }

  console.log('[Guard] User cannot access onboarding')
  return {
    allowed: false,
    redirect: PathConstants.Routes.HOME.value,
    reason: 'User does not have access to onboarding (needs pass plan and incomplete onboarding)'
  }
}

// ============================================
// GUARDS COMPOSTOS DO MÓDULO USER
// ============================================

export const userGuards = {
  /**
   * Exige que o usuário tenha pass plan e onboarding concluído.
   * É o guard mais comum para rotas autenticadas normais.
   */
  passPlanHolder: composeGuards(requirePassPlan, requireOnboardingCompleted),

  /**
   * Exige que o usuário tenha acesso ao onboarding.
   * O usuário precisa ter pass plan e onboarding não concluído.
   */
  onboardingAccess: requireOnboardingAccess,

  /**
   * Exige um portador de pass plan com uma feature flag específica.
   */
  passPlanHolderWithFeature: (flag: FeatureFlagType): Guard =>
    composeGuards(requirePassPlan, requireOnboardingCompleted, () => requireFeatureFlag(flag))
}
